import { useCallback, useEffect, useState } from 'react';

import AxiosFileSender from '../lib/AxiosFileSender.js';
import AxiosInstance from './AxiosInstance.js';

export default function useAPIList(baseURL, itemURL) {
  const [items, setItems] = useState([]);

  if (!itemURL) {
    itemURL = baseURL.replace(/\?.*$/, '');
  }

  const addItem = useCallback(
    (body) => {
      return AxiosFileSender.post(baseURL, body).then((res) => {
        setItems((prevItems) => prevItems.concat([res.data]));
        return res.data;
      });
    },
    [baseURL]
  );

  const editItem = useCallback(
    (id, body) => {
      return AxiosFileSender.put(`${itemURL}/${id}`, body).then((res) => {
        setItems((prevItems) => {
          if (typeof prevItems === 'object' && !Array.isArray(prevItems)) {
            const updated = {};
            let found = false;
            for (const key in prevItems) {
              if (Array.isArray(prevItems[key])) {
                const itemIndex = prevItems[key].findIndex(
                  (item) => item.id === id
                );
                if (itemIndex !== -1) {
                  updated[key] = prevItems[key].map((item) =>
                    item.id === id ? { ...item, ...res.data } : item
                  );
                  found = true;
                } else {
                  updated[key] = prevItems[key];
                }
              } else {
                updated[key] = prevItems[key];
              }
            }
            if (!found) {
              for (const key in updated) {
                if (Array.isArray(updated[key])) {
                  updated[key] = updated[key].concat([res.data]);
                  break;
                }
              }
            }
            return updated;
          }
          if (prevItems.find((item) => item.id === id))
            return prevItems.map((item) =>
              item.id === id ? { ...item, ...res.data } : item
            );
          else return prevItems.concat([res.data]);
        });
        return res.data;
      });
    },
    [itemURL]
  );

  const deleteItem = useCallback(
    (id) => {
      return AxiosInstance.delete(`${itemURL}/${id}`).then((res) => {
        setItems((prevItems) => {
          if (typeof prevItems === 'object' && !Array.isArray(prevItems)) {
            const updated = {};
            for (const key in prevItems) {
              if (Array.isArray(prevItems[key])) {
                updated[key] = prevItems[key].filter((i) => i.id !== id);
              } else {
                updated[key] = prevItems[key];
              }
            }
            return updated;
          }
          return prevItems.filter((i) => i.id !== id);
        });
        return res.data;
      });
    },
    [itemURL]
  );

  useEffect(() => {
    AxiosInstance.get(baseURL)
      .then((res) => setItems(res.data))
      .catch(console.error);
  }, [baseURL]);

  return [items, addItem, editItem, deleteItem, setItems];
}

export function useAPIListLanguage(baseURL) {
  const [items, setItems] = useState({}); // State now stores an object with language keys
  const itemURL = baseURL.replace('?showAll=true', '');

  const addItem = useCallback(
    (body, language, languageId) => {
      body.languageId = languageId;
      return AxiosFileSender.post(baseURL, body).then((res) => {
        setItems((prevItems) => ({
          ...prevItems,
          [language]: prevItems[language]
            ? prevItems[language].concat([res.data])
            : [res.data],
        }));
        return res.data;
      });
    },
    [baseURL]
  );

  const editItem = useCallback(
    (id, body, language, languageId) => {
      body.languageId = languageId;

      return AxiosFileSender.put(`${itemURL}/${id}`, body).then((res) => {
        setItems((prevItems) => ({
          ...prevItems,
          [language]: prevItems[language]
            ? prevItems[language].map((item) =>
                item.id === id ? { ...item, ...res.data } : item
              )
            : [res.data],
        }));
        return res.data;
      });
    },
    [itemURL]
  );

  const deleteItem = useCallback(
    (id, language) => {
      return AxiosInstance.delete(`${itemURL}/${id}`).then((res) => {
        setItems((prevItems) => ({
          ...prevItems,
          [language]: prevItems[language]?.filter((i) => i.id !== id) || [],
        }));
        return res.data;
      });
    },
    [itemURL]
  );

  useEffect(() => {
    AxiosInstance.get(baseURL)
      .then((res) => {
        setItems(res.data || {});
      })
      .catch(console.error);
  }, [baseURL]);

  return [items, addItem, editItem, deleteItem, setItems];
}
